import React, { useEffect, useReducer, useState } from 'react';

import { FlexColumn } from '@editor/components/utilities/layouts';
import analytics from '@repo/analytics';
import { Button } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2, P2 } from '@repo/ui/components/Typography';
import { Modal } from '@repo/ui/deprecated/Modal';
import { buildWebsiteUrl, LICENSING_PAGE_URL } from '@repo/utils/url';

import {
  LICENSING_ACCEPTED_TERMS,
  LICENSING_DISMISSED_TERMS,
} from '../../global/analytics/events';
import { ACCEPTED_TERMS_ACTION_KEY } from '../../global/constants';
import {
  actionStoreSelector,
  useActionStore,
} from '../../stores/actionStore/actionStore';
import { userStoreSelector, useUserStore } from '../../stores/userStore';
import Checkbox from '../Checkbox/Checkbox';

import { CheckboxWrapper } from './styles';
import { LicensingModalProps } from './types';
import {
  getDismissedCookie,
  hasAcceptedNewTerms,
  setDismissedCookie,
} from './utils';

const TERMS_OF_USE_URL = buildWebsiteUrl('/terms');
const CANCELLATION_URL = buildWebsiteUrl('/cancellation');

const LicensingModal: React.FC<LicensingModalProps> = ({ dismissable }) => {
  const [hasAcceptedTerms, toggleHasAcceptedTerms] = useReducer(
    (state) => !state,
    false
  );

  const user = useUserStore(userStoreSelector.user);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const actions = useActionStore(actionStoreSelector.actionIds);
  const createActon = useActionStore(actionStoreSelector.createAction);

  // we need this check since this code is sometimes reached during SSR
  let pathName = '';
  if (typeof window !== 'undefined') {
    pathName = window.location.pathname;
  }

  useEffect(() => {
    if (actions === null || !user) {
      return;
    }

    const isOnLegalScreens = [
      new URL(LICENSING_PAGE_URL).pathname,
      new URL(TERMS_OF_USE_URL).pathname,
      new URL(CANCELLATION_URL).pathname,
    ].includes(pathName ?? '');
    if (isOnLegalScreens) {
      setIsModalOpen(false);
      return;
    }

    const shouldOpenModal =
      !hasAcceptedNewTerms(user, actions) &&
      (!dismissable || getDismissedCookie() !== 'true');

    setIsModalOpen(shouldOpenModal);
  }, [user, actions, dismissable, pathName]);

  if (!user) {
    return null;
  }

  const handleClose = (): void => {
    setIsModalOpen(false);

    setDismissedCookie('true');

    void analytics.track(LICENSING_DISMISSED_TERMS);
  };

  const handleAcceptClick = (): void => {
    setIsModalOpen(false);

    void analytics.track(LICENSING_ACCEPTED_TERMS);

    void createActon(ACCEPTED_TERMS_ACTION_KEY);
  };

  return (
    <Modal
      externalCloseButton
      isOpen={isModalOpen}
      width="816px"
      onClose={dismissable ? handleClose : undefined}
    >
      <FlexColumn
        alignItems="center"
        style={{ padding: '40px', textAlign: 'center' }}
      >
        <H2>Kittl's New Licensing: More Power to Your Creativity</H2>
        <Spacer height="48px" />
        <P2>
          We believe in empowering your creative journey and carefully reviewed
          our new Terms of Use, Licensing, and Cancellation Policy to provide
          you with all the rights and permissions you need. Please take a moment
          to review and approve them.
        </P2>
        <Spacer height="48px" />
        <CheckboxWrapper>
          <Checkbox value={hasAcceptedTerms} onClick={toggleHasAcceptedTerms} />
          <P2>
            I have read, understood, and accept{' '}
            <a href={TERMS_OF_USE_URL} rel="noreferrer" target="_blank">
              Terms of Use
            </a>
            ,{' '}
            <a href={LICENSING_PAGE_URL} rel="noreferrer" target="_blank">
              Licensing
            </a>
            , and{' '}
            <a href={CANCELLATION_URL} rel="noreferrer" target="_blank">
              Cancellation Policy.
            </a>
          </P2>
        </CheckboxWrapper>
        <Spacer height="24px" />
        <Button
          disabled={!hasAcceptedTerms}
          label="Approve"
          name="approveLicensingButton"
          width="260px"
          onClick={handleAcceptClick}
        />
      </FlexColumn>
    </Modal>
  );
};

export default LicensingModal;
